/* eslint-disable no-restricted-syntax */

import React, { useState, useContext } from "react";
import { useTranslation } from "react-i18next";

import {
  Grid,
  Typography,
  Table,
  TableHead,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import AddRoundedIcon from "@material-ui/icons/AddRounded";
import DeleteIcon from "@material-ui/icons/Delete";
import _ from "lodash";
import { SectionGridContainer } from "../../components/Layout";
import CreateScheduleRules from "./CreateScheduleRules";
import { FleetContext, GeofencesContext } from "../../context";
import compactOvernightRules from "../../util/lists/compactRulesHumanReadable";
import compactRules from "../../util/lists/compactRules";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import {
  ABBREVIATED_DAYS,
  ABBREVIATED_MONTHS,
  ABBREVIATED_MONTHS_OF_YEAR,
  ABBREVIATED_DAYS_OF_WEEK,
} from "../../constants";

import convertScheduleTz from "../../util/parsers/convertScheduleTz";
import spTheme from "../../spTheme";
import formatContiguousValues from "../../util/parsers/formatContiguousValues";
import { sortWeekDaysAndMonths } from "../../util/helpers";

const ScheduleDisplay = () => {
  const useStyles = makeStyles((theme) => ({
    addButton: {
      background: "white",
      border: "1px solid transparent",
      "&:hover": {
        border: "1px solid white",
        color: "white",
      },
    },
    tableContainer: { maxHeight: theme.spacing(26), overflowY: "scroll" },
    tableHeader: {
      "& .MuiTableCell-head": {
        color: "white",
        backgroundColor: spTheme.palette.background.paper,
      },
    },
  }));

  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const { layers, selectedZone, deleteAPISchedule, scheduledRules } = useContext(GeofencesContext);
  const { activeFleet } = useContext(FleetContext);

  const layer = layers[selectedZone.parent];
  const zoneKeys = Object.keys(layer.zones);
  const activeGeofence = layer.zones;
  const activeZoneKey = zoneKeys.find((key) => key === selectedZone.key);
  const activeZoneGeofence = activeGeofence[activeZoneKey];
  const fleetTimezone = activeFleet.timezone;
  const rulesGroupedById = _.groupBy(scheduledRules, "schedule.id");
  const scheduleIdToName = _.mapValues(rulesGroupedById, (groupedObjtoList) => {
    const scheduleNameList = groupedObjtoList.map((rule) => rule.schedule.name);
    return scheduleNameList.length > 0 ? scheduleNameList[0] : "";
  });

  const formatTime = (timeString) => timeString.slice(0, 5);
  const ascending = (a, b) => a - b;

  const minifiedRulesByID = _.mapValues(rulesGroupedById, (rules) =>
    rules.map((rule) => ({
      ruleCount: 1,
      startTime: rule.start_time ? formatTime(rule.start_time) : null,
      endTime: rule.end_time ? formatTime(rule.end_time) : null,
      week_days: _.uniq(rule.week_days),
      month_days: _.sortBy(_.uniq(rule.month_days).map(Number), ascending),
      months: _.uniq(rule.months),
    })),
  );

  const geofenceTzRules = _.mapValues(
    minifiedRulesByID,
    _.flow(
      (rules) => convertScheduleTz({ rules }, "UTC", fleetTimezone),
      compactRules,
      compactOvernightRules,
    ),
  );

  const handleDelete = async (scheduleId) => {
    setConfirmationModalOpen(true);
    setScheduleToDelete(scheduleId);
  };

  const deleteSchedule = async ({ geofenceId, scheduleId }) => {
    setLoading(true);
    await deleteAPISchedule({ geofenceId, scheduleId });
    setLoading(false);
    setScheduleToDelete(null);
    setConfirmationModalOpen(false);
  };

  const rowData = _.flatten(
    _.values(
      _.mapValues(geofenceTzRules, ({ rules }, id) => rules.map((rule) => ({ ...rule, id }))),
    ),
  ).map((rule) => {
    const weekDays = rule.week_days;
    const monthDays = rule.month_days;
    const months = rule.months;

    const sortedWeekDays = sortWeekDaysAndMonths(weekDays);
    let datesActive = "";
    if (weekDays.length > 0) {
      const formatedDays = formatContiguousValues(
        sortedWeekDays.map((day) => ABBREVIATED_DAYS[day]),
        ABBREVIATED_DAYS_OF_WEEK,
      ).join(", ");

      datesActive = formatedDays;
    } else {
      datesActive = monthDays.join(", ");
    }

    const sortedMonths = sortWeekDaysAndMonths(months);
    const formatedMonths = formatContiguousValues(
      sortedMonths.map((month) => ABBREVIATED_MONTHS[month]),
      ABBREVIATED_MONTHS_OF_YEAR,
    ).join(", ");

    return {
      id: rule.id,
      rowKey: `${rule.id}-${rule.week_days.join("-")}-${rule.month_days.join("-")}`,
      timeActive: `${rule.startTime} - ${rule.endTime}`,
      monthsActive: formatedMonths,
      ruleCount: rule.ruleCount,
      name: scheduleIdToName[rule.id],
      datesActive,
      length: rule.duration,
    };
  });

  return (
    <SectionGridContainer spaceContents>
      <Grid container spacing={2}>
        <Grid item>
          <Typography variant="h6">{t("Fleet.Geofences.ScheduleDisplay.ScheduleRules")}</Typography>
        </Grid>
        <Grid item>
          <IconButton
            size="small"
            color="primary"
            className={classes.addButton}
            onClick={() => setModalOpen(!modalOpen)}
          >
            <AddRoundedIcon />
          </IconButton>
        </Grid>
      </Grid>
      <Grid container className={classes.tableContainer}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.Name")}</TableCell>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.TimeActive")}</TableCell>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.Length")}</TableCell>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.DatesActive")}</TableCell>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.MonthsActive")}</TableCell>
              <TableCell>{t("Fleet.Geofences.ScheduleDisplay.Rules")}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rowData.map((data) => (
              <TableRow key={data.rowKey}>
                <TableCell>{data.name}</TableCell>
                <TableCell>{data.timeActive}</TableCell>
                <TableCell>{data.length}</TableCell>
                <TableCell>{data.datesActive}</TableCell>
                <TableCell>{data.monthsActive}</TableCell>
                <TableCell>{data.ruleCount}</TableCell>
                <TableCell>
                  <IconButton size="small" color="secondary" onClick={() => handleDelete(data.id)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <ConfirmationModal
          title={t("Fleet.Geofences.ScheduleDisplay.ConfirmDelete")}
          label={t("Fleet.Geofences.ScheduleDisplay.ConfirmDelete")}
          content={t("Fleet.Geofences.ScheduleDisplay.ConfirmDeleteContent")}
          modalOpen={confirmationModalOpen}
          handleModalClose={() => setConfirmationModalOpen(false)}
          submit={() =>
            deleteSchedule({
              geofenceId: activeZoneGeofence.id,
              scheduleId: scheduleToDelete,
            })
          }
        />
      </Grid>
      <Grid item xs={12} style={{ padding: "0px" }}>
        {loading && <LinearProgress color="secondary" />}
      </Grid>
      {modalOpen && <CreateScheduleRules setModalOpen={setModalOpen} />}
    </SectionGridContainer>
  );
};

export default ScheduleDisplay;
